import { useCallback } from 'react';

import { createTagInFirebase } from '@/utils/createTagInFirebase';
import { getFirebaseClientByClientId } from '@/utils/getFirebaseClientByClientId';
import { getFirebaseRecordingTypesByIds } from '@/utils/getFirebaseRecordingTypesByIds';
import { getFirebaseSensorsBySystem } from '@/utils/getFirebaseSensorsBySystem';
import { getFirebaseSessionTypeById } from '@/utils/getFirebaseSessionTypeById';
import { getFirebaseSessionTypes } from '@/utils/getFirebaseSessionTypes';
import { getFirebaseSessionTypesByIds } from '@/utils/getFirebaseSessionTypesByIds';
import { SensorInDatabaseWithTitle, SystemInDatabase } from '@/utils/getFirebaseSystemByConfigId';
import { getFirebaseSystemConfigByConfigId } from '@/utils/getFirebaseSystemConfigByConfigId';
import { getFirebaseSystemsByOrganizationId } from '@/utils/getFirebaseSystemsByOrganizationId';
import { getFirebaseTag } from '@/utils/getFirebaseTag';
import { getFirebaseTagsForOrganization } from '@/utils/getFirebaseTagsForOrganization';

import { useSensorsContext } from '@/contexts/SensorsContext';
import { useSystemConfigContext } from '@/contexts/SystemConfigContext';

export function useDatabase() {
  const { setSensorsInDatabase, setNetworkKey, setDeviceState } = useSensorsContext();
  const { setConfig } = useSystemConfigContext();

  const fetchSystem = useCallback(
    async (organizationId: string, sessionTypeId: string): Promise<SystemInDatabase | undefined> => {
      const systems = await getFirebaseSystemsByOrganizationId(organizationId);
      console.log('Systems:', systems);

      if (!systems || systems.length === 0) {
        return undefined;
      }

      // Find the system that matches the session type
      const systemForSessionType = systems.find((system) => system.sessionTypes.includes(sessionTypeId));

      if (!systemForSessionType) {
        return undefined;
      }

      // Fetch the config for the system
      // TODO: Add proper error handling
      if (systemForSessionType.config) {
        const config = await getFirebaseSystemConfigByConfigId(systemForSessionType.config);

        if (config && config.deviceState) {
          setConfig(config);
          setDeviceState(config.deviceState);
        } else {
          setDeviceState('0x00F89800'); // Acc: 8G Gyro: 1000dps
        }
      } else {
        console.log('System does not have config');
      }

      // Set the network key in the sensors context
      setNetworkKey(systemForSessionType.networkKey);

      // Fetch the sensors for the system
      const sensors = await getFirebaseSensorsBySystem(systemForSessionType);
      setSensorsInDatabase(sensors.filter((sensor): sensor is SensorInDatabaseWithTitle => sensor !== undefined));

      return systemForSessionType;
    },
    [],
  );

  return {
    fetchSystem,
    getFirebaseSystemsByOrganizationId,
    getFirebaseRecordingTypesByIds,
    getFirebaseSessionTypeById,
    getFirebaseSessionTypesByIds,
    getFirebaseSessionTypes,
    getFirebaseSystemConfigByConfigId,
    getFirebaseTag,
    getFirebaseTagsForOrganization,
    createTagInFirebase,
    getFirebaseClientByClientId,
  };
}
